/**
 * Los colores de las gráficas, siguiendo el tema claro u oscuro.
 *
 * POR QUÉ ESTO NO SE ARREGLÓ CON LA CAPA DE CSS DE LA ETAPA 1
 * ===========================================================
 *
 * Toda la etapa 1 funcionó con una técnica: una capa de CSS que traduce las
 * clases (`bg-white` → la superficie oscura, etc.). Con las gráficas **esa
 * técnica no puede funcionar**, y conviene entender por qué antes de intentar
 * arreglarlo por ahí otra vez:
 *
 * Recharts no pinta clases, pinta **atributos SVG** —`stroke="#F1F5F9"`,
 * `fill="#3B82F6"`— generados desde JavaScript. Un atributo no es una clase: el
 * CSS no lo alcanza. Y `var(--mi-color)` tampoco sirve dentro de un atributo de
 * presentación SVG, así que no hay atajo.
 *
 * La salida es al revés: **leer** la variable de CSS desde JavaScript y pasarle
 * a la gráfica el color ya resuelto. Es lo que hace este módulo.
 *
 * CÓMO SE ENTERA DE QUE CAMBIÓ EL TEMA
 * ====================================
 *
 * Con un `MutationObserver` sobre la clase de `<html>`, no con el estado de
 * React del interruptor. La razón es concreta: el interruptor vive en la barra
 * superior y las gráficas viven varios niveles más abajo, en otra rama del
 * árbol. Pasar el estado por props obligaría a atravesar media aplicación, y un
 * contexto nuevo sería otra pieza que mantener. La clase en `<html>` ya es la
 * fuente de verdad para todo lo demás —el CSS entero cuelga de ella—, así que
 * observarla es leer la misma verdad, no inventar una segunda.
 *
 * LO QUE NO SE PUEDE COMPROBAR EN LAS PRUEBAS
 * ==========================================
 *
 * jsdom —donde corre el arnés de render— **no resuelve variables de CSS**:
 * `getComputedStyle` sobre `var(--cv-grafica-1)` devuelve vacío. Ya pasó al
 * medir el contraste en la etapa 1 y se documentó entonces. Por eso este módulo
 * lleva colores de respaldo escritos a mano: si la variable no resuelve, la
 * gráfica se pinta igual con el color de siempre en vez de quedarse invisible.
 * Las pruebas verifican la LÓGICA (que lee variables, que se resuscribe al
 * cambio, que hay respaldo); el color final solo se puede ver en un navegador
 * de verdad.
 */
import { useCallback, useEffect, useState } from 'react';

/** Los nombres de las variables y su color de respaldo (el de modo claro).
 *  El respaldo importa: sin él, un fallo al resolver dejaría gráficas sin
 *  color, que es peor que un color poco ideal. */
const SERIES = [
  ['--cv-grafica-1', '#0F172A'],
  ['--cv-grafica-2', '#3B82F6'],
  ['--cv-grafica-3', '#10B981'],
  ['--cv-grafica-4', '#F59E0B'],
  ['--cv-grafica-5', '#EF4444'],
  ['--cv-grafica-6', '#8B5CF6'],
];

const ESTRUCTURA = {
  rejilla: ['--cv-grafica-rejilla', '#F1F5F9'],
  eje: ['--cv-grafica-eje', '#64748B'],
  fondoGlobo: ['--cv-surface', '#FFFFFF'],
  textoGlobo: ['--cv-text', '#0F172A'],
  bordeGlobo: ['--cv-border', '#E2E8F0'],
};

function resolver(nombre, respaldo) {
  try {
    const v = getComputedStyle(document.documentElement)
      .getPropertyValue(nombre)
      .trim();
    return v || respaldo;
  } catch {
    return respaldo;
  }
}

function leerPaleta() {
  const paleta = {
    series: SERIES.map(([n, r]) => resolver(n, r)),
  };
  for (const [clave, [n, r]] of Object.entries(ESTRUCTURA)) {
    paleta[clave] = resolver(n, r);
  }
  return paleta;
}

export function usePaletaDeGraficas() {
  const [paleta, setPaleta] = useState(leerPaleta);

  const releer = useCallback(() => setPaleta(leerPaleta()), []);

  useEffect(() => {
    // Se vuelve a leer cuando cambia la clase de `<html>`, que es exactamente
    // el momento en que el modo oscuro entra o sale.
    let observador;
    try {
      observador = new MutationObserver(releer);
      observador.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    } catch {
      /* sin MutationObserver (entornos de prueba antiguos) se queda con lo leído */
    }
    return () => { try { observador?.disconnect(); } catch { /* ya desconectado */ } };
  }, [releer]);

  return paleta;
}

/** Para código que no es un componente de React. */
export { leerPaleta };
